import { radarToWorld, worldToRadar, type Level, type MapId } from "@69ly/maps";
import { actorAt, type Grenade, type Plan, type PlanAction, type PlanPoint, type Schedule } from "@69ly/shared";
import { useRef, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { floorOf, isPlayer, radarRadius, screenYaw, yawTowards, type PlanActor } from "../../lib/plan.ts";
import { GrenadeGlyph } from "../GrenadeGlyph.tsx";
import { OnMap, Radar, type RadarFocus } from "../Radar.tsx";

/** How big each grenade's effect is drawn, in world units: a smoke's cloud, the fire, a flash's pop. */
const EFFECT_RADIUS: Record<Grenade, number> = { smoke: 144, molotov: 120, flash: 160, he: 110 };
/** How far ahead of a player the aim handle sits, in world units. */
const AIM_REACH = 260;
/** Seconds a call stays up next to the player. */
const CALL_SECONDS = 2.5;

/** A spot on the map that can be picked up and moved: a start, a move's end, the end of an aim. */
function DragHandle({
  map,
  at,
  className,
  label,
  onDrag,
  onEnd,
  children,
}: {
  map: MapId;
  at: { x: number; y: number };
  className: string;
  label: string;
  onDrag: (p: { x: number; y: number }) => void;
  onEnd: () => void;
  children?: ReactNode;
}) {
  const layer = useRef<HTMLElement | null>(null);
  const moved = useRef(false);
  const move = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!layer.current || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const r = layer.current.getBoundingClientRect();
    moved.current = true;
    onDrag(radarToWorld(map, { u: (e.clientX - r.left) / r.width, v: (e.clientY - r.top) / r.height }));
  };
  return (
    <OnMap map={map} x={at.x} y={at.y}>
      <button
        type="button"
        className={className}
        aria-label={label}
        title={label}
        onPointerDown={(e) => {
          e.stopPropagation();
          layer.current = e.currentTarget.closest<HTMLElement>(".radar-layer");
          moved.current = false;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={move}
        onPointerUp={(e) => {
          e.currentTarget.releasePointerCapture(e.pointerId);
          if (moved.current) onEnd();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </button>
    </OnMap>
  );
}

export interface StageEdit {
  /** A click on the map itself (not on a handle), in world units on the floor shown. */
  onPick: (p: PlanPoint) => void;
  /** A start or a move's end dragged. */
  onPoint: (actor: string, key: "start" | string, p: PlanPoint) => void;
  /** A start's or a hold's direction dragged (CS2 yaw). */
  onAim: (actor: string, key: "start" | string, yaw: number) => void;
  /** The drag is over: one step for undo. */
  onDone: () => void;
}

/** A single or combo offered on the map while building: its throw spot, a click adds it. */
export interface StageTarget {
  key: string;
  kind: "throw" | "combo";
  id: string;
  title: string;
  grenade: Grenade;
  x: number;
  y: number;
  z: number | null;
}

export function PlanStage({
  map,
  mapName,
  level,
  plan,
  schedule,
  elapsed,
  focus,
  selected,
  view,
  actorTitle,
  edit,
  targets,
  onTarget,
  onSelectActor,
  pickZ,
  children,
}: {
  map: MapId;
  mapName: string;
  level: Level;
  plan: Plan;
  schedule: Schedule;
  elapsed: number;
  /** Everyone else dimmed. */
  focus: string | null;
  /** Its handles shown (building). */
  selected: string | null;
  view?: RadarFocus;
  actorTitle: (a: PlanActor) => string;
  edit?: StageEdit;
  targets?: StageTarget[];
  onTarget?: (t: StageTarget) => void;
  /** Building: a click on a player's token picks them. */
  onSelectActor?: (id: string) => void;
  /** The height to give spots picked on this floor (Nuke's lower level). */
  pickZ: number | null;
  /** More on the map, over everything (Live: the throw being looked at). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const k = 1000;
  const actors: PlanActor[] = [...plan.players, ...plan.opponents];
  const onFloor = (z: number | null | undefined) => floorOf(map, z ?? null) === level;
  const r = (p: { x: number; y: number }) => worldToRadar(map, p.x, p.y);
  const dim = (id: string) => focus !== null && focus !== id;
  const clock = schedule.toClock(elapsed);
  const bombKnown = plan.bombKnownAt === null || clock <= plan.bombKnownAt;

  // Each actor's path: where they start, then the end of each walk.
  const paths = actors.map((a) => {
    const s = schedule.actors.get(a.id);
    const pts = [a.start, ...(s?.segments.filter((x) => x.kind === "move" || x.kind === "travel").map((x) => x.to) ?? [])].filter((p) => onFloor(p.z));
    return { a, pts };
  });

  return (
    <Radar map={map} mapName={mapName} level={level} focus={view} onPick={edit ? (p) => edit.onPick({ ...radarToWorld(map, p), z: pickZ }) : undefined}>
      <svg className="plan-svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
        {/* Throws: from the spot to where it lands, faint until it flies. */}
        {schedule.throws.map((th, i) => {
          if (!onFloor(th.from.z)) return null;
          const a = r(th.from);
          const b = r(th.to);
          return <line key={`tl${i}`} className={`plan-throw gl-${th.grenade}${dim(th.actor) ? " is-dim" : ""}`} x1={a.u * k} y1={a.v * k} x2={b.u * k} y2={b.v * k} />;
        })}
        {paths.map(({ a, pts }) =>
          pts.length > 1 ? (
            <polyline
              key={`p${a.id}`}
              className={`plan-path ${isPlayer(a) ? `slot-${a.slot}` : "is-enemy"}${dim(a.id) ? " is-dim" : ""}${selected === a.id ? " is-selected" : ""}`}
              points={pts.map((p) => `${r(p).u * k},${r(p).v * k}`).join(" ")}
            />
          ) : null,
        )}
        {/* Building: where the selected player looks, at the start and on each hold. */}
        {edit &&
          actors
            .filter((a) => a.id === selected)
            .flatMap((a) => {
              const s = schedule.actors.get(a.id);
              const tone = isPlayer(a) ? `slot-${a.slot}` : "is-enemy";
              const aims: { key: string; from: { x: number; y: number; z?: number | null }; yaw: number }[] = [{ key: "start", from: a.start, yaw: a.start.yaw }];
              for (const x of a.actions as PlanAction[]) {
                if (x.kind !== "hold") continue;
                const seg = s?.segments.find((g) => g.action === x.id && g.kind === "hold");
                if (seg) aims.push({ key: x.id, from: seg.from, yaw: x.yaw });
              }
              return aims
                .filter((m) => onFloor(m.from.z ?? null))
                .map((m) => {
                  const rad = (m.yaw * Math.PI) / 180;
                  const p = r(m.from);
                  const q = r({ x: m.from.x + Math.cos(rad) * AIM_REACH, y: m.from.y + Math.sin(rad) * AIM_REACH });
                  return <line key={`aim${m.key}`} className={`plan-aim-line ${tone}`} x1={p.u * k} y1={p.v * k} x2={q.u * k} y2={q.v * k} />;
                });
            })}
        {/* Grenades: in the air, then their effect while it lasts. */}
        {schedule.throws.map((th, i) => {
          if (elapsed < th.at || elapsed >= th.gone || !onFloor(th.to.z ?? th.from.z)) return null;
          const a = r(th.from);
          const b = r(th.to);
          if (elapsed < th.lands) {
            const q = (elapsed - th.at) / Math.max(0.01, th.lands - th.at);
            // A gentle arc: up in the middle of the way.
            const lift = Math.sin(Math.PI * q) * 0.02;
            return <circle key={`g${i}`} className={`plan-nade gl-${th.grenade}`} cx={(a.u + (b.u - a.u) * q) * k} cy={(a.v + (b.v - a.v) * q - lift) * k} r={5} />;
          }
          const life = (elapsed - th.lands) / Math.max(0.01, th.gone - th.lands);
          const rad = radarRadius(map, EFFECT_RADIUS[th.grenade]) * k;
          const grow = th.grenade === "smoke" ? Math.min(1, (elapsed - th.lands) / 1.2) : 1;
          return (
            <circle
              key={`e${i}`}
              className={`plan-effect gl-${th.grenade}`}
              cx={b.u * k}
              cy={b.v * k}
              r={rad * grow}
              style={{ opacity: th.grenade === "flash" || th.grenade === "he" ? 1 - life : life > 0.85 ? (1 - life) / 0.15 : 1 } as CSSProperties}
            />
          );
        })}
      </svg>

      {plan.bomb && onFloor(plan.bomb.z) && (
        <OnMap map={map} x={plan.bomb.x} y={plan.bomb.y}>
          <span className={`plan-bomb${bombKnown ? "" : " is-unknown"}`} title={bombKnown ? t("plan.bomb") : t("plan.bombUnknown")}>
            {bombKnown ? "C4" : "C4 ?"}
          </span>
        </OnMap>
      )}

      {targets?.map((x) =>
        onFloor(x.z) ? (
          <OnMap key={x.key} map={map} x={x.x} y={x.y}>
            <button
              type="button"
              className={`plan-target gl-${x.grenade}${x.kind === "combo" ? " is-combo" : ""}`}
              title={x.title}
              aria-label={t(x.kind === "combo" ? "plan.addComboHere" : "plan.addThrowHere", { title: x.title })}
              onClick={(e) => {
                e.stopPropagation();
                onTarget?.(x);
              }}
            >
              <GrenadeGlyph grenade={x.grenade} size={11} />
            </button>
          </OnMap>
        ) : null,
      )}

      {/* The selected actor's handles: start, each move's end, each direction. */}
      {edit &&
        actors
          .filter((a) => a.id === selected)
          .flatMap((a) => {
            const out: ReactNode[] = [];
            const tone = isPlayer(a) ? `slot-${a.slot}` : "is-enemy";
            if (onFloor(a.start.z)) {
              out.push(
                <DragHandle key="start" map={map} at={a.start} className={`plan-handle is-start ${tone}`} label={t("plan.dragStart")} onDrag={(p) => edit.onPoint(a.id, "start", { ...p, z: a.start.z ?? null })} onEnd={edit.onDone} />,
              );
              const rad = (a.start.yaw * Math.PI) / 180;
              const aim = { x: a.start.x + Math.cos(rad) * AIM_REACH, y: a.start.y + Math.sin(rad) * AIM_REACH };
              out.push(<DragHandle key="start-aim" map={map} at={aim} className={`plan-handle is-aim ${tone}`} label={t("plan.dragStartAim")} onDrag={(p) => edit.onAim(a.id, "start", yawTowards(a.start, p))} onEnd={edit.onDone} />);
            }
            const s = schedule.actors.get(a.id);
            for (const x of a.actions as PlanAction[]) {
              if (x.kind === "move" && onFloor(x.to.z)) {
                out.push(
                  <DragHandle key={x.id} map={map} at={x.to} className={`plan-handle is-point ${tone}`} label={t("plan.dragPoint")} onDrag={(p) => edit.onPoint(a.id, x.id, { ...p, z: x.to.z ?? null })} onEnd={edit.onDone} />,
                );
              }
              if (x.kind === "hold") {
                const seg = s?.segments.find((g) => g.action === x.id && g.kind === "hold");
                if (!seg || !onFloor(seg.from.z)) continue;
                const rad = (x.yaw * Math.PI) / 180;
                const aim = { x: seg.from.x + Math.cos(rad) * AIM_REACH, y: seg.from.y + Math.sin(rad) * AIM_REACH };
                out.push(<DragHandle key={`${x.id}-aim`} map={map} at={aim} className={`plan-handle is-aim ${tone}`} label={t("plan.dragHoldAim")} onDrag={(p) => edit.onAim(a.id, x.id, yawTowards(seg.from, p))} onEnd={edit.onDone} />);
              }
            }
            return out;
          })}

      {/* Everyone where they are now, facing where they look. */}
      {actors.map((a) => {
        const s = schedule.actors.get(a.id);
        if (!s) return null;
        const now = actorAt(s, elapsed);
        if (!onFloor(now.z)) return null;
        const call = schedule.calls.filter((c) => c.actor === a.id && elapsed >= c.at && elapsed < c.at + CALL_SECONDS).pop();
        const className = `pp-token plan-token ${isPlayer(a) ? `slot-${a.slot}` : "is-enemy"}${dim(a.id) ? " is-dim" : ""}${selected === a.id ? " is-selected" : ""}${now.moving ? " is-moving" : ""}`;
        const style = { "--angle": `${screenYaw(map, now, now.yaw)}deg` } as CSSProperties;
        const inside = (
          <>
            <span className="pp-facing" aria-hidden="true" />
            <span className="pp-number">{isPlayer(a) ? a.slot : "E"}</span>
            {call && <span className="plan-call">{call.text}</span>}
          </>
        );
        return (
          <OnMap key={`t${a.id}`} map={map} x={now.x} y={now.y}>
            {onSelectActor && selected !== a.id ? (
              <button
                type="button"
                className={`${className} is-pickable`}
                style={style}
                title={actorTitle(a)}
                aria-label={t("plan.selectActor", { name: actorTitle(a) })}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectActor(a.id);
                }}
              >
                {inside}
              </button>
            ) : (
              <span className={className} style={style} title={actorTitle(a)}>
                {inside}
              </span>
            )}
          </OnMap>
        );
      })}
      {children}
    </Radar>
  );
}
